import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Select, Tooltip, Upload, message } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";

import * as api from "./controller";
import HtmlBodyEditor from "./HtmlBodyEditor";
import "./mailboxes.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Trimmed, de-duplicated (case-insensitively), in the order given.
export function uniqueEmails(list) {
  const seen = new Set();
  const out = [];
  (list || []).forEach((raw) => {
    const e = String(raw || "").trim();
    const k = e.toLowerCase();
    if (e && !seen.has(k)) { seen.add(k); out.push(e); }
  });
  return out;
}

// Who a new email can go to, grouped for the To dropdown: this office's contact
// cards, its own email and tracked addresses first, then contacts at the other
// offices of the same practice (each labelled with its office). Anyone not
// listed can still be typed in.
export function recipientGroups({ officeId, contacts, practiceContacts, officeEmail, tracked, practiceName }) {
  const seen = new Set();
  const opt = (email, text) => {
    const e = String(email || "").trim();
    const k = e.toLowerCase();
    if (!e || seen.has(k)) return null;
    seen.add(k);
    return { value: e, label: text ? `${text} — ${e}` : e };
  };
  const who = (c) => [c.name, c.title].filter(Boolean).join(" · ");
  const here = [];
  const push = (list, o) => { if (o) list.push(o); };
  (contacts || []).forEach((c) => push(here, opt(c.email, who(c))));
  (practiceContacts || []).filter((c) => c.office_id === officeId)
    .forEach((c) => push(here, opt(c.email, who(c))));
  push(here, opt(officeEmail, "Office email"));
  (tracked || []).forEach((e) => push(here, opt(e, "Tracked address")));
  const elsewhere = [];
  (practiceContacts || []).filter((c) => c.office_id !== officeId).forEach((c) => {
    const name = who(c);
    push(elsewhere, opt(c.email, [name, c.office_name ? `(${c.office_name})` : ""].filter(Boolean).join(" ")));
  });
  const groups = [];
  if (here.length) groups.push({ label: "This office", options: here });
  if (elsewhere.length) {
    groups.push({
      label: practiceName ? `Other offices in ${practiceName}` : "Other offices in this practice",
      options: elsewhere,
    });
  }
  return groups;
}

// The same for a lead: its own address, then its contact cards.
export function leadRecipientGroups({ leadEmail, contacts }) {
  const seen = new Set();
  const options = [];
  const add = (email, text) => {
    const e = String(email || "").trim();
    if (!e || seen.has(e.toLowerCase())) return;
    seen.add(e.toLowerCase());
    options.push({ value: e, label: text ? `${text} — ${e}` : e });
  };
  add(leadEmail, "Lead email");
  (contacts || []).forEach((c) => add(c.email, [c.name, c.title].filter(Boolean).join(" · ")));
  return options.length ? [{ label: "This lead", options }] : [];
}

// Who a reply goes to, for showing in the modal. The server (Graph) decides for
// real — replying to their email honours a reply-to address — but this is what
// it will be in every ordinary case.
export function replyRecipients(email, replyAll) {
  if (!email) return [];
  const me = ((email.mailbox && email.mailbox.address) || "").toLowerCase();
  const addr = (p) => (p && p.address) || "";
  const fromThem = email.direction !== "outbound";
  const list = fromThem
    ? [addr(email.from)].concat(replyAll ? (email.to || []).concat(email.cc || []).map(addr) : [])
    : (email.to || []).map(addr).concat(replyAll ? (email.cc || []).map(addr) : []);
  return uniqueEmails(list.filter((a) => a && a.toLowerCase() !== me));
}

/**
 * New email or reply, for an office or a lead.
 *
 *  - New email: `officeId` (or `leadId`), `defaultTo`, and the address book
 *    (`contacts`, `officeEmail`, `practice`, or `leadEmail` for a lead).
 *  - Reply: `replyTo` — one email from the list — and `replyAll`. It answers
 *    THAT email, from the inbox that holds it.
 *
 * Exported for its regression test (__tests__/composeAndToday.test.js).
 */
export function EmailComposeModal({
  open, onClose, onSent, officeId, leadId, defaultTo, contacts, officeEmail, practice, leadEmail,
  replyTo, replyAll,
}) {
  const isReply = !!replyTo;
  const [to, setTo] = useState(() => uniqueEmails(defaultTo));
  const [practiceContacts, setPracticeContacts] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [busy, setBusy] = useState(false);
  // Which of our CRM inboxes a NEW email goes out from. Replies need no choice:
  // they leave from the inbox that received the email being answered.
  const [inboxes, setInboxes] = useState([]);
  const [mailboxId, setMailboxId] = useState(null);

  // Reset the form when the modal OPENS — and only then. This used to re-run
  // whenever `defaultTo` changed identity, and the drawer rebuilds that array on
  // every render, so any re-render of the page behind the modal erased whatever
  // had just been typed into To, Subject and the message.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTo(uniqueEmails(defaultTo));
      setSubject("");
      setBody("");
      setAttachmentIds([]);
    }
    wasOpen.current = open;
  }, [open, defaultTo]);

  // Contacts at the practice's other offices, for the To dropdown. The practice
  // endpoint already returns every office's contact cards tagged with its office.
  const practiceId = practice && practice.id;
  useEffect(() => {
    if (!open || isReply || !practiceId || leadId) return undefined;
    let cancelled = false;
    api.getPracticeDetail(practiceId).then((r) => {
      if (cancelled) return;
      const ok = r.status >= 200 && r.status < 300 && r.json && Array.isArray(r.json.contacts);
      setPracticeContacts(ok ? r.json.contacts : []);
    });
    return () => { cancelled = true; };
  }, [open, isReply, practiceId, leadId]);

  useEffect(() => {
    if (!open || isReply) return undefined;
    let cancelled = false;
    api.getMailboxes({ kind: "crm", active: true }).then((r) => {
      if (cancelled || !(r.status >= 200 && r.status < 300 && Array.isArray(r.json))) return;
      setInboxes(r.json);
      const dflt = r.json.find((m) => m.is_default_sender) || r.json[0];
      setMailboxId(dflt ? dflt.id : null);
    });
    return () => { cancelled = true; };
  }, [open, isReply]);

  const uploadProps = {
    multiple: true,
    customRequest: async ({ file, onSuccess, onError }) => {
      const r = await api.uploadEmailAttachment(file);
      if (r.status >= 200 && r.status < 300) {
        setAttachmentIds((ids) => [...ids, r.json.id]);
        onSuccess(r.json);
      } else {
        message.error((r.json && r.json.detail) || "Attachment upload failed.");
        onError(new Error("upload failed"));
      }
    },
    onRemove: (file) => {
      const id = file.response && file.response.id;
      if (id) setAttachmentIds((ids) => ids.filter((x) => x !== id));
    },
  };

  const send = async () => {
    if (!body.trim()) {
      message.error("Write a message.");
      return;
    }
    if (!isReply) {
      const toList = uniqueEmails(to);
      if (!toList.length) {
        message.error("Add at least one recipient.");
        return;
      }
      const bad = toList.filter((e) => !EMAIL_RE.test(e));
      if (bad.length) {
        message.error(`Not an email address: ${bad.join(", ")}`);
        return;
      }
    }
    setBusy(true);
    let res;
    if (isReply) {
      res = await api.replyToEmail(replyTo.id, {
        body_html: body, reply_all: !!replyAll, attachment_ids: attachmentIds,
      });
    } else {
      const payload = {
        to: uniqueEmails(to), subject, body_html: body, attachment_ids: attachmentIds,
        ...(mailboxId ? { mailbox_id: mailboxId } : {}),
      };
      res = leadId ? await api.composeLeadEmail(leadId, payload) : await api.composeOfficeEmail(officeId, payload);
    }
    setBusy(false);
    if (res.status >= 200 && res.status < 300) {
      message.success(isReply ? "Reply sent." : "Email sent.");
      onSent && onSent(res.json);
    } else if (res.status === 503) {
      message.warning("Email integration isn't enabled yet.");
    } else {
      message.error((res.json && res.json.detail) || "Send failed.");
    }
  };

  const toOptions = leadId
    ? leadRecipientGroups({ leadEmail, contacts })
    : recipientGroups({
      officeId, contacts, practiceContacts, officeEmail, tracked: defaultTo,
      practiceName: practice && practice.name,
    });
  const replyTo_ = isReply ? replyRecipients(replyTo, replyAll) : [];

  return (
    <Modal
      title={isReply ? (replyAll ? "Reply all" : "Reply") : "New email"}
      open={open}
      onCancel={onClose}
      onOk={send}
      confirmLoading={busy}
      okText="Send"
      width={620}
      zIndex={10050}
    >
      {isReply ? (
        <>
          <div className="mbx-from">
            <span className="mbx-from-label">From</span>
            <Tooltip title="Replies go out from the inbox that received the email.">
              <span>{(replyTo.mailbox && replyTo.mailbox.address) || "Default inbox"}</span>
            </Tooltip>
          </div>
          <div className="mbx-from">
            <span className="mbx-from-label">To</span>
            <span className="mbx-reply-to">{replyTo_.join(", ") || "—"}</span>
          </div>
          <div className="mbx-from">
            <span className="mbx-from-label">Re</span>
            <span className="mbx-reply-to">{replyTo.subject || "(no subject)"}</span>
          </div>
        </>
      ) : null}
      {!isReply && inboxes.length > 0 ? (
        <div className="mbx-from">
          <span className="mbx-from-label">From</span>
          {inboxes.length > 1 ? (
            <Select
              value={mailboxId}
              onChange={setMailboxId}
              options={inboxes.map((m) => ({
                value: m.id,
                label: m.display_name ? `${m.display_name} <${m.mailbox_address}>` : m.mailbox_address,
              }))}
              // Above the drawer (z-index 10001) and this modal (10050).
              dropdownStyle={{ zIndex: 10060 }}
              style={{ minWidth: 280 }}
            />
          ) : (
            <span>{inboxes[0].mailbox_address}</span>
          )}
        </div>
      ) : null}
      {!isReply ? (
        <>
          <div className="mbx-from">
            <span className="mbx-from-label">To</span>
            {/* Pick from the address book, or type any address — Enter, comma
                or space adds it. */}
            <Select
              mode="tags"
              value={to}
              onChange={(vals) => setTo(uniqueEmails(vals))}
              options={toOptions}
              optionLabelProp="value"
              optionFilterProp="label"
              tokenSeparators={[",", ";", " "]}
              placeholder="Pick a contact or type an address"
              notFoundContent={<span className="crm-sub">No saved contacts match — type the address and press Enter.</span>}
              // Above the drawer (z-index 10001) and this modal (10050).
              dropdownStyle={{ zIndex: 10060 }}
              style={{ flex: 1, minWidth: 0 }}
            />
          </div>
          <div className="mbx-from">
            <span className="mbx-from-label">Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
        </>
      ) : null}
      <HtmlBodyEditor
        // Remounted on each open, so it always starts ready to type.
        key={open ? "open" : "closed"}
        initialMode="plain"
        value={body}
        onChange={setBody}
        rows={7}
        placeholder="Write your message (HTML allowed)…"
        style={{ marginTop: 8 }}
      />
      <div style={{ marginTop: 10 }}>
        <Upload {...uploadProps}>
          <Button icon={<PaperClipOutlined />} size="small">Attach file</Button>
        </Upload>
      </div>
    </Modal>
  );
}

export default EmailComposeModal;
